'use client';

import React, { useMemo, useState } from 'react';
import { Box, Button, Popover, Typography } from '@mui/material';
import { CounselSlotIndicator } from '@/entities/visit-counsel';
import dayjs from 'dayjs';
import { Calendar } from '@/shared/ui/schedule/Calendar';

export interface WorkScheduleDayData {
  regions: string[];
  regionIds?: string[];
  counsels: number;
}

export interface WorkScheduleCalendarProps {
  /** 현재 표시 월 (YYYY-MM) */
  month: string;
  /** 선택된 날짜 (YYYY-MM-DD) */
  value: string | null;
  /** 날짜 선택 시 */
  onChange: (date: string) => void;
  /** 월 변경 시 */
  onMonthChange?: (month: string) => void;
  /** 날짜별 근무 데이터 */
  scheduleData?: Record<string, WorkScheduleDayData>;
  /** 모바일 여부 - 축약 표시 */
  isMobile?: boolean;
  /** 헤더 숨김 (외부에서 직접 제어 시) */
  hideHeader?: boolean;
  /** 수정 클릭 시 (해당 날짜에 근무가 있을 때만 표시) */
  onEditClick?: (date: string) => void;
  /** 삭제 클릭 시 (해당 날짜에 근무가 있을 때만 표시) */
  onDeleteClick?: (date: string, regionIds: string[]) => void;
  /** true면 셀 클릭 시 Popover를 열지 않음 (모바일 액션바 통합 시 사용) */
  disablePopover?: boolean;
}

function formatRegionsDisplay(regions: string[], isMobile: boolean): string {
  if (isMobile) {
    return regions.length === 1 ? regions[0] : `${regions[0]}+${regions.length - 1}`;
  }
  if (regions.length < 3) return regions.join(', ');
  return `${regions[0]}+${regions.length - 1}`;
}

/**
 * 근무표 캘린더 (플래너 도메인).
 * 구역(다중) + 배정 상담 수(●) 표시, 셀 클릭 Popover.
 * 캘린더 그리드는 shared/ui/Calendar를 사용합니다.
 */
export function WorkScheduleCalendar({
  month,
  value,
  onChange,
  onMonthChange,
  scheduleData = {},
  isMobile = false,
  hideHeader = false,
  onEditClick,
  onDeleteClick,
  disablePopover = false,
}: WorkScheduleCalendarProps) {
  const today = dayjs().format('YYYY-MM-DD');
  const m = dayjs(month);
  const monthStart = m.startOf('month').format('YYYY-MM-DD');
  const monthEnd = m.endOf('month').format('YYYY-MM-DD');

  const { totalCounsels, counselsBeforeToday } = useMemo(() => {
    let total = 0;
    let beforeToday = 0;
    for (const [dateKey, data] of Object.entries(scheduleData)) {
      if (dateKey < monthStart || dateKey > monthEnd) continue;
      const c = data?.counsels ?? 0;
      total += c;
      if (dateKey < today) beforeToday += c;
    }
    return { totalCounsels: total, counselsBeforeToday: beforeToday };
  }, [scheduleData, monthStart, monthEnd, today]);

  const [cellPopover, setCellPopover] = useState<{
    anchor: HTMLElement;
    dateKey: string;
    regions: string[];
    regionIds: string[];
    counsels: number;
  } | null>(null);

  return (
    <>
      <Calendar
        month={month}
        value={value}
        onChange={onChange}
        onMonthChange={onMonthChange}
        isMobile={isMobile}
        hideHeader={hideHeader}
        renderHeaderDescription={() => (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            총 {totalCounsels}건{counselsBeforeToday > 0 && ` · 오늘 이전 ${counselsBeforeToday}건`}
          </Typography>
        )}
        renderDayContent={(dateKey, { isMobile: mob }) => {
          const data = scheduleData[dateKey];
          const hasWork = data && data.regions.length > 0 && !data.regions.includes('-');
          const displayLabel = hasWork ? formatRegionsDisplay(data!.regions, mob) : '';
          const counsels = data?.counsels ?? 0;

          return (
            <>
              {hasWork && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontSize: mob ? '0.55rem' : '0.65rem',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                  }}
                >
                  {displayLabel}
                </Typography>
              )}
              {counsels > 0 && (
                <Box
                  component="span"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ '&:hover': { opacity: 0.85 } }}
                >
                  <CounselSlotIndicator variant="count" count={counsels} isMobile={mob} />
                </Box>
              )}
            </>
          );
        }}
        onDayClick={
          disablePopover
            ? undefined
            : (dateKey, e) => {
                const data = scheduleData[dateKey];
                setCellPopover({
                  anchor: e.currentTarget,
                  dateKey,
                  regions: data?.regions ?? [],
                  regionIds: data?.regionIds ?? [],
                  counsels: data?.counsels ?? 0,
                });
              }
        }
      />

      <Popover
        open={!!cellPopover}
        anchorEl={cellPopover?.anchor}
        onClose={() => setCellPopover(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ p: 1.5, maxWidth: 280 }}>
          {cellPopover && (
            <>
              <Typography variant="body2" sx={{ mb: cellPopover.regions.length > 0 ? 0.5 : 0 }}>
                구역: {cellPopover.regions.length > 0 ? cellPopover.regions.join(', ') : '비근무'}
              </Typography>
              <Typography variant="body2" sx={{ mb: cellPopover.regions.length > 0 ? 1 : 0 }}>
                상담 {cellPopover.counsels}건
              </Typography>
              {cellPopover.regions.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {onEditClick && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        onEditClick(cellPopover.dateKey);
                        setCellPopover(null);
                      }}
                    >
                      수정
                    </Button>
                  )}
                  {onDeleteClick && cellPopover.regionIds.length > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        onDeleteClick(cellPopover.dateKey, cellPopover.regionIds);
                        setCellPopover(null);
                      }}
                    >
                      삭제
                    </Button>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}
